import {
  linearTiming,
  type TransitionPresentation,
  type TransitionTiming,
} from "@remotion/transitions";
import {
  TransitionDirection,
  TransitionDto,
  TransitionKind,
  TransitionSound,
  TransitionSpeed,
} from "@repo/api/transition";
import type { CSSProperties, FC } from "react";
import { msToFrames } from "../../utils/timing.ts";
import { easeInOutCubic } from "../../utils/easing.ts";
import { ImageFadeTransitionBuilder } from "./ImageFadeTransitionBuilder.ts";
import { ImagePushTransitionBuilder } from "./ImagePushTransitionBuilder.ts";
import { ImageWipeTransitionBuilder } from "./ImageWipeTransitionBuilder.ts";
import { ImageFadeBlackTransitionBuilder } from "./ImageFadeBlackTransitionBuilder.tsx";
import { ImageZoomInOutTransitionBuilder } from "./ImageZoomInOutTransitionBuilder.ts";
import { ImageZoomInFadeTransitionBuilder } from "./ImageZoomInFadeTransitionBuilder.ts";
import { ImageNoneTransitionBuilder } from "./ImageNoneTransitionBuilder.ts";
import { ImageFadeWhiteTransitionBuilder } from "./ImageFadeWhiteTransitionBuilder.tsx";
import { ImageLightLeakTransitionBuilder } from "./ImageLightLeakTransitionBuilder.tsx";
import { ImageZoomBlurTransitionBuilder } from "./ImageZoomBlurTransitionBuilder.ts";
import { ImageZoomOutBlurTransitionBuilder } from "./ImageZoomOutBlurTransitionBuilder.ts";
import { ImageGlitchCutTransitionBuilder } from "./ImageGlitchCutTransitionBuilder.ts";
import { ImageFocusPullTransitionBuilder } from "./ImageFocusPullTransitionBuilder.ts";
import { ImageWhipPanTransitionBuilder } from "./ImageWhipPanTransitionBuilder.ts";
import { ImageClockWipeTransitionBuilder } from "./ImageClockWipeTransitionBuilder.ts";
import { ImageSplitRevealTransitionBuilder } from "./ImageSplitRevealTransitionBuilder.ts";
import { ImageSplitScreenWipeTransitionBuilder } from "./ImageSplitScreenWipeTransitionBuilder.ts";

export interface TransitionFormConfig {
  kind: TransitionKind;
  buildDefaultValue: () => TransitionDto;
  speed?: TransitionSpeed[];
  direction?: TransitionDirection[];
  intensity?: boolean;
  seed?: boolean;
}

export interface ImageTransitionBuilderProps {
  imageTransition: TransitionDto;
  fps: number;
}

export interface ImageTransitionPreviewClipDurations {
  previewClip1DurationInFrames: number;
  previewClip2DurationInFrames: number;
}

export interface ImageTransition<T extends Record<string, unknown>> {
  getDurationInFrames: (props: ImageTransitionBuilderProps) => number;
  buildPresentation: (
    props: ImageTransitionBuilderProps,
  ) => TransitionPresentation<T>;
  buildTiming: (props: ImageTransitionBuilderProps) => TransitionTiming;
}

export interface ImageTransitionWrapperProps extends ImageTransitionBuilderProps {
  imageDurationInFrames: number;
  currentFrame: number;
}

export interface ImageWrapper {
  overlayComponent?: FC<ImageTransitionWrapperProps>;
  buildWrapperStyle?: (
    props: ImageTransitionWrapperProps,
  ) => CSSProperties | null;
}

export interface ImageTransitionWrappers {
  prevImageWrapper?: ImageWrapper;
  nextImageWrapper?: ImageWrapper;
}

export interface ImageTransitionBuilder<T extends Record<string, unknown>> {
  formConfig: TransitionFormConfig;
  transition?: ImageTransition<T>;
  wrappers?: ImageTransitionWrappers;
  getPreviewClipDurationsInFrames: (
    props: ImageTransitionBuilderProps,
  ) => ImageTransitionPreviewClipDurations;
}

export const DEFAULT_TRANSITION_DURATION_MS = new Map([
  ["SLOW", 2000],
  ["NORMAL", 1000],
  ["FAST", 500],
]);

export function getDefaultDurationInFrames(props: ImageTransitionBuilderProps) {
  const speed = props.imageTransition.speed;
  if (!speed) {
    throw new Error(
      "Image transition speed is required when using getDefaultDurationInFrames",
    );
  }

  const duration = DEFAULT_TRANSITION_DURATION_MS.get(speed);
  if (!duration) {
    throw new Error(`Unknown image transition speed: ${speed}`);
  }

  return msToFrames(duration, props.fps);
}

export function getDefaultPreviewClipDurationsInFrames(
  props: ImageTransitionBuilderProps,
): ImageTransitionPreviewClipDurations {
  const speed = props.imageTransition.speed;
  if (!speed) {
    throw new Error(
      "Image transition speed is required when using getDefaultPreviewClipDurationsInFrames",
    );
  }

  if (speed === "SLOW") {
    return {
      previewClip1DurationInFrames: msToFrames(2400, props.fps),
      previewClip2DurationInFrames: msToFrames(2400, props.fps),
    };
  } else if (speed === "NORMAL") {
    return {
      previewClip1DurationInFrames: msToFrames(1600, props.fps),
      previewClip2DurationInFrames: msToFrames(2000, props.fps),
    };
  } else if (speed === "FAST") {
    return {
      previewClip1DurationInFrames: msToFrames(1400, props.fps),
      previewClip2DurationInFrames: msToFrames(1800, props.fps),
    };
  } else {
    throw new Error(`Unknown image transition speed: ${speed}`);
  }
}

export function getDefaultTiming(props: ImageTransitionBuilderProps) {
  return linearTiming({
    durationInFrames: getDefaultDurationInFrames(props),
    easing: easeInOutCubic,
  });
}

export const ImageTransitionBuilders = new Map([
  [TransitionKind.enum.NONE, ImageNoneTransitionBuilder],
  [TransitionKind.enum.FADE, ImageFadeTransitionBuilder],
  [TransitionKind.enum.FADE_BLACK, ImageFadeBlackTransitionBuilder],
  [TransitionKind.enum.FADE_WHITE, ImageFadeWhiteTransitionBuilder],
  [TransitionKind.enum.PUSH, ImagePushTransitionBuilder],
  [TransitionKind.enum.WIPE, ImageWipeTransitionBuilder],
  [TransitionKind.enum.ZOOM_IN_FADE, ImageZoomInFadeTransitionBuilder],
  [TransitionKind.enum.ZOOM_IN_OUT, ImageZoomInOutTransitionBuilder],
  [TransitionKind.enum.ZOOM_BLUR, ImageZoomBlurTransitionBuilder],
  [TransitionKind.enum.ZOOM_OUT_BLUR, ImageZoomOutBlurTransitionBuilder],
  [TransitionKind.enum.GLITCH_CUT, ImageGlitchCutTransitionBuilder],
  [TransitionKind.enum.FOCUS_PULL, ImageFocusPullTransitionBuilder],
  [TransitionKind.enum.WHIP_PAN, ImageWhipPanTransitionBuilder],
  [TransitionKind.enum.CLOCK_WIPE, ImageClockWipeTransitionBuilder],
  [TransitionKind.enum.SPLIT_REVEAL, ImageSplitRevealTransitionBuilder],
  [
    TransitionKind.enum.SPLIT_SCREEN_WIPE,
    ImageSplitScreenWipeTransitionBuilder,
  ],
  [TransitionKind.enum.LIGHT_LEAK, ImageLightLeakTransitionBuilder],
]);

export const IMAGE_TRANSITION_DEFAULT_SOUNDS: Record<
  TransitionKind,
  TransitionSound | undefined
> = {
  NONE: undefined,
  FADE: undefined,
  FADE_BLACK: undefined,
  FADE_WHITE: undefined,
  PUSH: "SHORT_SOFT_WHOOSH",
  WIPE: "SWOOSH_1",
  ZOOM_IN_FADE: "SOFT_CINEMATIC_WHOOSH",
  ZOOM_IN_OUT: "SWOOSH_1",
  ZOOM_BLUR: "FAST_AIR_SWOOSH",
  ZOOM_OUT_BLUR: "SOFT_CINEMATIC_WHOOSH",
  GLITCH_CUT: "SHORT_GLITCH",
  FOCUS_PULL: "SOFT_CINEMATIC_WHOOSH",
  WHIP_PAN: "FAST_AIR_SWOOSH",
  CLOCK_WIPE: "SWOOSH_1",
  SPLIT_REVEAL: "EPIC_CINEMATIC_BOOM",
  SPLIT_SCREEN_WIPE: "SWOOSH_1",
  LIGHT_LEAK: "SOFT_CINEMATIC_WHOOSH",
};

// Shown first in the editor's sound picker; any sound can still be picked for any transition.
export const IMAGE_TRANSITION_RECOMMENDED_SOUNDS: Record<
  TransitionKind,
  TransitionSound[]
> = {
  NONE: [],
  FADE: ["SOFT_CINEMATIC_WHOOSH", "SHORT_SOFT_WHOOSH", "SOFT_WHOOSH"],
  FADE_BLACK: ["SOFT_CINEMATIC_WHOOSH", "SHORT_SOFT_WHOOSH"],
  FADE_WHITE: ["SOFT_CINEMATIC_WHOOSH", "SHORT_SOFT_WHOOSH", "CAMERA_FLASH"],
  PUSH: [
    "SHORT_SOFT_WHOOSH",
    "SWOOSH_1",
    "FAST_AIR_SWOOSH",
    "SHORT_SOFT_TRANSITION",
  ],
  WIPE: ["SWOOSH_1", "SHORT_SOFT_WHOOSH", "FAST_AIR_SWOOSH", "SOFT_WHOOSH"],
  ZOOM_IN_FADE: [
    "SOFT_CINEMATIC_WHOOSH",
    "SHORT_SOFT_WHOOSH",
    "SOFT_RISER_WHOOSH",
  ],
  ZOOM_IN_OUT: [
    "SWOOSH_1",
    "SOFT_CINEMATIC_WHOOSH",
    "EPIC_CINEMATIC_BOOM",
    "CINEMATIC_IMPACT",
  ],
  ZOOM_BLUR: [
    "FAST_AIR_SWOOSH",
    "SOFT_CINEMATIC_WHOOSH",
    "EPIC_CINEMATIC_BOOM",
  ],
  ZOOM_OUT_BLUR: ["SOFT_CINEMATIC_WHOOSH", "FAST_AIR_SWOOSH"],
  GLITCH_CUT: [
    "SHORT_GLITCH",
    "DIGITAL_GLITCH_SWIPE",
    "SUBTLE_FUTURISTIC_BLIP",
    "CLEAN_TECH_WHOOSH",
  ],
  FOCUS_PULL: [
    "SOFT_CINEMATIC_WHOOSH",
    "SUBTLE_FUTURISTIC_BLIP",
    "CAMERA_SHUTTER",
    "VINTAGE_FILM_CAMERA",
  ],
  WHIP_PAN: ["FAST_AIR_SWOOSH", "SWOOSH_1", "CLEAN_UI_WHOOSH"],
  CLOCK_WIPE: [
    "SWOOSH_1",
    "SUBTLE_FUTURISTIC_BLIP",
    "SOFT_TICKING",
    "CLEAN_TICKING",
  ],
  SPLIT_REVEAL: [
    "EPIC_CINEMATIC_BOOM",
    "SOFT_CINEMATIC_WHOOSH",
    "EPIC_CINEMATIC_BOOM_2",
    "CINEMATIC_BASS_HIT",
    "MAGICAL_REVEAL",
  ],
  SPLIT_SCREEN_WIPE: ["SWOOSH_1", "FAST_AIR_SWOOSH"],
  LIGHT_LEAK: [
    "SOFT_CINEMATIC_WHOOSH",
    "SHORT_SOFT_WHOOSH",
    "ELEGANT_RISER",
    "ARCANE_CHIME",
    "MYSTICAL_AMBIENCE",
  ],
};

// Where in the transition (0-1) the sound's peak lands; defaults to the middle.
export const IMAGE_TRANSITION_SOUND_PEAK_AT: Partial<
  Record<TransitionKind, number>
> = {
  // Most blurred while the entering scene starts its fast zoom-out.
  ZOOM_OUT_BLUR: 0.25,
};
